import { useEffect, useMemo } from "react";
import { useForm, Controller, useWatch } from "react-hook-form";
import {
    TextField,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Switch,
    Stack,
    Typography,
    Autocomplete,
    Box,
    Grid,
    Paper,
    MenuItem,
} from "@mui/material";
import { SettingsSuggest as ServiceIcon } from "@mui/icons-material";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useProjects } from "../../../hooks/useProjects";
import { useServicesTypes } from "../../../hooks/administration/useServicesTypes";

const schema = yup.object().shape({
    serpronom: yup.string().required("El nombre es obligatorio").max(255),
    serprodes: yup.string().nullable().max(500),
    serpropre: yup
        .number()
        .typeError("Debe ser número")
        .min(0, "No puede ser negativo")
        .required("El precio es requerido"),
    serprouni: yup
        .string()
        .oneOf(["VIAJE", "HORA", "KM", "UNIDAD"])
        .required("La unidad es obligatoria"),
    proid: yup.number().typeError("Seleccione un proyecto").required("Seleccione un proyecto"),
    tipserid: yup.number().typeError("Seleccione un tipo de servicio").required("Seleccione un tipo de servicio"),
    serproper: yup.boolean(),
    serproint: yup.boolean(),
});

const ServicesTypeProjectForm = ({ open, handleClose, onSubmit, defaultValues = {} }) => {
    const isEditing = Boolean(defaultValues?.serproid);

    const { projects = [], isLoading: loadingProjects } = useProjects();
    const { servicesTypes = [], isLoading: loadingTypes } = useServicesTypes();

    const {
        register,
        control,
        handleSubmit,
        reset,
        formState: { errors, isSubmitting },
    } = useForm({
        resolver: yupResolver(schema),
        defaultValues: {
            serpronom: "",
            serprodes: "",
            proid: null,
            tipserid: null,
            serpropre: 0,
            serprouni: "VIAJE",
            serproper: true,
            serproint: false,
        },
    });

    const watchProId = useWatch({ control, name: "proid" });
    const watchTipSerId = useWatch({ control, name: "tipserid" });

    useEffect(() => {
        if (open) {
            reset({
                serpronom: defaultValues.serpronom || "",
                serprodes: defaultValues.serprodes || "",
                proid: defaultValues.proid ? Number(defaultValues.proid) : null,
                tipserid: defaultValues.tipserid ? Number(defaultValues.tipserid) : null,
                serpropre: defaultValues.serpropre ?? 0,
                serprouni: defaultValues.serprouni || "VIAJE",
                serproper: typeof defaultValues.serproper === "boolean" ? defaultValues.serproper : true,
                serproint: typeof defaultValues.serproint === "boolean" ? defaultValues.serproint : false,
            });
        }
    }, [open, defaultValues, reset]);

    const selectedProject = useMemo(
        () => projects.find((p) => Number(p.proid) === Number(watchProId)) || null,
        [projects, watchProId]
    );

    const selectedServiceType = useMemo(
        () => servicesTypes.find((t) => Number(t.tipserid) === Number(watchTipSerId)) || null,
        [servicesTypes, watchTipSerId]
    );

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            fullWidth
            maxWidth="sm"
            PaperProps={{ sx: { borderRadius: 2 } }}
        >
            <DialogTitle sx={{ fontWeight: "bold", display: "flex", alignItems: "center", gap: 1 }}>
                <ServiceIcon color="primary" />
                {isEditing ? "Editar Servicio de Proyecto" : "Nuevo Servicio de Proyecto"}
            </DialogTitle>

            <form onSubmit={handleSubmit(onSubmit)}>
                <DialogContent dividers sx={{ bgcolor: "#fbfbfb" }}>
                    <Stack spacing={3}>
                        <TextField
                            label="Nombre del servicio de proyecto"
                            fullWidth
                            {...register("serpronom")}
                            error={!!errors.serpronom}
                            helperText={errors.serpronom?.message}
                            placeholder="Ej: FESA || QUELLAVECO 8X8"
                            autoFocus
                        />

                        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                            <Controller
                                name="proid"
                                control={control}
                                render={({ field }) => (
                                    <Autocomplete
                                        fullWidth
                                        disabled={isEditing}
                                        options={projects}
                                        loading={loadingProjects}
                                        value={selectedProject}
                                        isOptionEqualToValue={(opt, val) =>
                                            Number(opt.proid) === Number(val?.proid)
                                        }
                                        getOptionLabel={(opt) => opt.pronom || ""}
                                        onChange={(_, val) => field.onChange(val?.proid ?? null)}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Proyecto"
                                                error={!!errors.proid}
                                                helperText={errors.proid?.message}
                                            />
                                        )}
                                    />
                                )}
                            />

                            <Controller
                                name="tipserid"
                                control={control}
                                render={({ field }) => (
                                    <Autocomplete
                                        fullWidth
                                        disabled={isEditing}
                                        options={servicesTypes}
                                        loading={loadingTypes}
                                        value={selectedServiceType}
                                        isOptionEqualToValue={(opt, val) =>
                                            Number(opt.tipserid) === Number(val?.tipserid)
                                        }
                                        getOptionLabel={(opt) => opt.tipsernom || ""}
                                        onChange={(_, val) => field.onChange(val?.tipserid ?? null)}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Tipo de servicio"
                                                error={!!errors.tipserid}
                                                helperText={errors.tipserid?.message}
                                            />
                                        )}
                                    />
                                )}
                            />
                        </Stack>

                        <TextField
                            label="Descripción"
                            fullWidth
                            multiline
                            rows={2}
                            {...register("serprodes")}
                            error={!!errors.serprodes}
                            helperText={errors.serprodes?.message}
                        />

                        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                            <TextField
                                label="Precio unitario"
                                type="number"
                                fullWidth
                                inputProps={{ step: "0.01", min: "0" }}
                                {...register("serpropre", {
                                    valueAsNumber: true
                                })}
                                {...register("serpropre")}
                                error={!!errors.serpropre}
                                helperText={errors.serpropre?.message}
                            />

                            <TextField
                                select
                                label="Unidad"
                                fullWidth
                                defaultValue="VIAJE"
                                {...register("serprouni")}
                                error={!!errors.serprouni}
                                helperText={errors.serprouni?.message}
                            >
                                <MenuItem value="VIAJE">Viaje</MenuItem>
                                <MenuItem value="HORA">Hora</MenuItem>
                                <MenuItem value="KM">Kilómetro</MenuItem>
                                <MenuItem value="UNIDAD">Unidad</MenuItem>
                            </TextField>
                        </Stack>

                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <Paper
                                    variant="outlined"
                                    sx={{
                                        p: 1.5,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        borderRadius: 2,
                                    }}
                                >
                                    <Typography variant="body2" fontWeight={500}>
                                        Permanente
                                    </Typography>

                                    <Controller
                                        name="serproper"
                                        control={control}
                                        render={({ field }) => (
                                            <Switch
                                                checked={!!field.value}
                                                onChange={(e) => field.onChange(e.target.checked)}
                                                size="small"
                                            />
                                        )}
                                    />
                                </Paper>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <Paper
                                    variant="outlined"
                                    sx={{
                                        p: 1.5,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        borderRadius: 2,
                                    }}
                                >
                                    <Typography variant="body2" fontWeight={500}>
                                        Interno
                                    </Typography>

                                    <Controller
                                        name="serproint"
                                        control={control}
                                        render={({ field }) => (
                                            <Switch
                                                checked={!!field.value}
                                                onChange={(e) => field.onChange(e.target.checked)}
                                                size="small"
                                            />
                                        )}
                                    />
                                </Paper>
                            </Grid>
                        </Grid>
                    </Stack>
                </DialogContent>

                <DialogActions sx={{ p: 2.5, bgcolor: "#fbfbfb" }}>
                    <Button onClick={handleClose} color="inherit" variant="outlined">
                        Cancelar
                    </Button>

                    <Button type="submit" variant="contained" disabled={isSubmitting} disableElevation>
                        {isEditing ? "Actualizar" : "Guardar"}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};

export default ServicesTypeProjectForm;